/**
 * LoginScreen — tela de autenticação Nava.
 *
 * F0-FE-2: ligada ao AuthProvider.
 *   - login(email, password)   → fluxo de credenciais (stub local)
 *   - loginSso()               → SSO real via Microsoft Entra ID (redirect)
 *
 * O que aparece na tela é decidido pelo backend (`GET /auth/sso/config`):
 * sem AUTH_ENTRA_* configurado o botão de SSO some; com DISABLE_CREDENTIALS
 * o formulário de e-mail/senha some e o SSO vira o único caminho.
 *   - Redireciona para o destino original após autenticação (state.from do router)
 *   - Usuários Admin são interceptados pelo AuthGuard para MFA antes de entrar
 *
 * Extraído do protótipo preservando paleta, estilos inline e copy pt-BR.
 * Acessibilidade: labels associados, role="alert" no erro, onKeyDown Enter.
 */
import { useState, useEffect } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowRight, ShieldCheck, Eye, EyeOff, AlertCircle, Loader } from 'lucide-react';
import { C } from '@/theme/palette';
import { Card, Btn, NavaWordmark, NavaBackdrop } from '@/components/ui';
import { useAuth } from '@/auth/AuthContext';
import { MfaScreen } from '@/auth/MfaScreen';
import { fetchAuthConfig, ssoErrorMessage } from '@/auth/ssoClient';
import { displayVersion } from '@/lib/version';

const INPUT_STYLE = {
  width: '100%',
  backgroundColor: C.black,
  border: `1px solid ${C.blackBorder}`,
  color: C.neutral,
  padding: '10px 14px',
  borderRadius: 6,
  fontSize: 14,
  outline: 'none',
};

export function LoginScreen() {
  const { login, loginSso, isAuthenticated, isLoading, error, status } = useAuth();
  const location  = useLocation();
  const navigate  = useNavigate();

  const [email,    setEmail]    = useState('gabriel.costa@nava.com.br');
  const [password, setPassword] = useState('');
  const [show,     setShow]     = useState(false);

  const [searchParams] = useSearchParams();
  // O backend devolve /login?error=... quando o SSO falha ou é cancelado.
  const ssoError  = searchParams.get('error');
  // Escape hatch: /login?showform=1 reexibe o formulário mesmo em modo SSO-only
  // (útil para suporte entrar com uma conta local sem mexer em variável de ambiente).
  const forceForm = searchParams.get('showform') === '1';

  // Config de autenticação: até o backend responder, não renderizamos nada de
  // login — senão o formulário piscaria antes de sumir em ambientes SSO-only.
  const [authConfig, setAuthConfig] = useState(null);

  useEffect(() => {
    let cancelled = false;
    fetchAuthConfig().then((config) => {
      if (!cancelled) setAuthConfig(config);
    });
    return () => { cancelled = true; };
  }, []);

  const ssoEnabled         = authConfig?.ssoEnabled ?? false;
  const credentialsVisible = (authConfig?.credentialsEnabled ?? true) || forceForm;
  // SSO-only: o formulário está escondido e não há motivo para ficar na tela.
  const ssoOnly            = ssoEnabled && !credentialsVisible;

  // Em modo SSO-only mandamos direto para a Microsoft — exceto logo após um
  // erro, senão o usuário entraria num laço de redirect sem nunca ver a causa.
  useEffect(() => {
    if (ssoOnly && !ssoError) {
      loginSso(location.state?.from?.pathname ?? '/dashboard');
    }
  }, [ssoOnly, ssoError, loginSso, location.state]);

  // Redireciona para a rota original (ou dashboard) após autenticação
  useEffect(() => {
    if (isAuthenticated) {
      const from = location.state?.from?.pathname ?? '/dashboard';
      navigate(from, { replace: true });
    }
  }, [isAuthenticated, location.state, navigate]);

  // Se status for mfa-required, o AuthGuard já renderizará MfaScreen;
  // aqui garantimos que a LoginScreen não fique com estado de loading travado.

  const handleSubmit = () => {
    if (!email.trim() || !password) return;
    login(email, password);
  };

  const handleKey = (e) => {
    if (e.key === 'Enter') handleSubmit();
  };

  const handleSso = () => {
    loginSso(location.state?.from?.pathname ?? '/dashboard');
  };

  // Botão fica desabilitado durante loading OU enquanto aguarda MFA
  const submitting = isLoading || status === 'mfa-required';

  // Admin autenticou credenciais e precisa de MFA. Como /login é rota pública
  // (fora do AuthGuard), renderizamos a MfaScreen aqui — senão o usuário ficaria
  // preso em "Entrando..." sem nunca ver o desafio de MFA.
  if (status === 'mfa-required') {
    return <MfaScreen />;
  }

  // Config ainda carregando, ou redirect SSO-only em curso: spinner em vez de
  // um formulário que apareceria só para sumir no frame seguinte.
  if (authConfig === null || (ssoOnly && !ssoError)) {
    return (
      <div
        role="status"
        aria-label="Carregando..."
        style={{
          minHeight: '100vh',
          backgroundColor: C.black,
          color: C.neutralDim,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 14,
        }}
      >
        <Loader size={22} aria-hidden="true" />
        <span style={{ fontSize: 13 }}>
          {ssoOnly ? 'Redirecionando para a Microsoft...' : 'Carregando...'}
        </span>
      </div>
    );
  }

  return (
    <div
      style={{
        backgroundColor: C.black,
        minHeight: '100vh',
        color: C.neutral,
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <NavaBackdrop />

      <div
        style={{
          position: 'relative',
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 32,
        }}
      >
        <div style={{ width: '100%', maxWidth: 440, textAlign: 'center' }}>
          <div className="mb-12 flex justify-center">
            <NavaWordmark size={56} />
          </div>

          <h1
            style={{
              fontSize: 32,
              fontWeight: 700,
              letterSpacing: '-0.03em',
              lineHeight: 1.1,
              marginTop: 40,
              marginBottom: 12,
              color: C.neutral,
            }}
          >
            Plataforma de Engenharia de software
          </h1>

          <Card style={{ padding: 28 }}>
            {/* Erro devolvido pelo backend após um SSO malsucedido */}
            {ssoError && (
              <div
                role="alert"
                aria-live="assertive"
                style={{
                  marginBottom: 16,
                  padding: '10px 12px',
                  borderRadius: 6,
                  backgroundColor: `${C.hot}11`,
                  border: `1px solid ${C.hot}44`,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  textAlign: 'left',
                }}
              >
                <AlertCircle size={15} style={{ color: C.hot, flexShrink: 0 }} aria-hidden="true" />
                <span style={{ fontSize: 12, color: C.hotLight, lineHeight: 1.4 }}>
                  {ssoErrorMessage(ssoError)}
                </span>
              </div>
            )}

            {credentialsVisible && (
            <>
            {/* E-mail */}
            <div style={{ marginBottom: 16, textAlign: 'left' }}>
              <label
                htmlFor="login-email"
                style={{ fontSize: 12, color: C.neutralDim, display: 'block', marginBottom: 6 }}
              >
                E-mail corporativo
              </label>
              <input
                id="login-email"
                type="email"
                value={email}
                onChange={(e) => { setEmail(e.target.value); }}
                onKeyDown={handleKey}
                placeholder="nome.sobrenome@nava.com.br"
                autoComplete="email"
                disabled={submitting}
                style={{
                  ...INPUT_STYLE,
                  opacity: submitting ? 0.6 : 1,
                }}
              />
            </div>

            {/* Senha */}
            <div style={{ marginBottom: 16, textAlign: 'left' }}>
              <label
                htmlFor="login-password"
                style={{ fontSize: 12, color: C.neutralDim, display: 'block', marginBottom: 6 }}
              >
                Senha
              </label>
              <div style={{ position: 'relative' }}>
                <input
                  id="login-password"
                  type={show ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => { setPassword(e.target.value); }}
                  onKeyDown={handleKey}
                  placeholder="••••••••••"
                  autoComplete="current-password"
                  disabled={submitting}
                  style={{
                    ...INPUT_STYLE,
                    paddingRight: 40,
                    opacity: submitting ? 0.6 : 1,
                  }}
                />
                <button
                  type="button"
                  onClick={() => setShow((s) => !s)}
                  aria-label={show ? 'Ocultar senha' : 'Mostrar senha'}
                  tabIndex={submitting ? -1 : 0}
                  style={{
                    position: 'absolute',
                    right: 10,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    color: C.neutralDim,
                    background: 'transparent',
                    border: 'none',
                    display: 'flex',
                    cursor: 'pointer',
                  }}
                >
                  {show ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            {/* Erro de autenticação */}
            {error && (
              <div
                role="alert"
                aria-live="assertive"
                style={{
                  marginBottom: 16,
                  padding: '10px 12px',
                  borderRadius: 6,
                  backgroundColor: `${C.hot}11`,
                  border: `1px solid ${C.hot}44`,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  textAlign: 'left',
                }}
              >
                <AlertCircle size={15} style={{ color: C.hot, flexShrink: 0 }} aria-hidden="true" />
                <span style={{ fontSize: 12, color: C.hotLight, lineHeight: 1.4 }}>{error}</span>
              </div>
            )}

            <Btn
              variant="primary"
              size="lg"
              icon={submitting ? Loader : ArrowRight}
              onClick={handleSubmit}
              disabled={submitting || !email.trim() || !password}
            >
              {submitting ? 'Entrando...' : 'Entrar'}
            </Btn>
            </>
            )}

            {/* Separador só faz sentido quando as duas vias estão na tela */}
            {credentialsVisible && ssoEnabled && (
              <div
                style={{
                  margin: '16px 0',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                }}
              >
                <div style={{ flex: 1, height: 1, backgroundColor: C.blackBorder }} />
                <span style={{ fontSize: 11, color: C.neutralMuted }}>ou</span>
                <div style={{ flex: 1, height: 1, backgroundColor: C.blackBorder }} />
              </div>
            )}

            {/* SSO — Microsoft Entra ID (some quando AUTH_ENTRA_* não está configurado) */}
            {ssoEnabled && (
              <Btn
                variant={credentialsVisible ? 'ghost' : 'primary'}
                size="lg"
                icon={ShieldCheck}
                onClick={handleSso}
                disabled={submitting}
              >
                {authConfig?.providerLabel ?? 'Entrar com Microsoft'}
              </Btn>
            )}

            <div
              style={{
                marginTop: 14,
                paddingTop: 14,
                borderTop: `1px solid ${C.blackBorder}`,
                fontSize: 12,
                color: C.neutralMuted,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 6,
              }}
            >
              <ShieldCheck size={13} aria-hidden="true" />
              Autenticação OAuth 2.0 / OIDC · MFA obrigatório para Admins
            </div>
          </Card>

          <p style={{ fontSize: 15, color: C.neutralDim, lineHeight: 1.5, marginTop: 16 }}>
            Orquestração de agentes sobre código com Governança, Segurança e Auditoria completa.
          </p>

          <div
            style={{
              marginTop: 24,
              fontSize: 12,
              color: C.neutralMuted,
              display: 'flex',
              justifyContent: 'center',
              gap: 16,
            }}
          >
            <span>{displayVersion()}</span>
            <span>·</span>
            <span>Status: operacional</span>
            <span>·</span>
            <span>SOC2 Type I-ready</span>
          </div>
        </div>
      </div>
    </div>
  );
}
